import React, { useEffect, useRef, useState } from 'react';
import { useFont } from '../../contexts/FontContext';
import { getScorePanelStyles } from '../../utils/frameStyleUtils';

/**
 * GameScoreDisplay - Consistent score display component for HTML overlay games
 * Uses theme colors, fonts, and frame styling from frameStyleUtils.
 */

// Inject score animations once — CSS runs on compositor, no JS rAF needed
let scoreStyleInjected = false;
function ensureScoreStyles() {
  if (scoreStyleInjected) return;
  scoreStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes sf-score-pop { 0% { transform: scale(1); } 50% { transform: scale(1.12); } 100% { transform: scale(1); } }
    .sf-score-pop { animation: sf-score-pop 0.2s ease-out; }
    @keyframes sf-score-enter { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
    .sf-score-fadein { animation: sf-score-enter 0.3s ease-out both; }
  `;
  document.head.appendChild(style);
}
ensureScoreStyles();

// Animated number component — pure CSS (no Framer Motion rAF during gameplay)
const AnimatedScore = ({ value, font, color }) => {
  const spanRef = useRef(null);
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      const el = spanRef.current;
      if (el) {
        // Restart CSS animation by removing and re-adding class
        el.classList.remove('sf-score-pop');
        // Force reflow to restart animation
        void el.offsetWidth;
        el.classList.add('sf-score-pop');
      }
    }
  }, [value]);

  const digitCount = Math.max(3, String(Math.round(value)).length);
  const minWidth = `${digitCount}ch`;

  return (
    <span
      ref={spanRef}
      style={{
        fontFamily: font ? `"${font}", sans-serif` : 'inherit',
        color: color,
        display: 'inline-block',
        minWidth: minWidth,
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {Math.round(value).toLocaleString()}
    </span>
  );
};

const GameScoreDisplay = ({
  score = 0,
  scoreDisplayRef, // When provided: ref-based DOM update, no React re-renders on score change
  label = null, // Optional label like "Score" or "Points"
  colors = {},
  font = 'Inter',
  fontScale, // Custom font scale from settings (optional)
  frameStyle = 'clean',
  position = 'top-center', // 'top-center', 'top-left', 'top-right'
  size = 'medium', // 'small', 'medium', 'large'
  animate = true,
  showBackground = true,
  className = '',
}) => {
  const { loadFont, isFontLoaded } = useFont();
  const _fontLoaded = isFontLoaded(font);

  // Load font on mount using centralized font loader
  useEffect(() => {
    loadFont(font);
  }, [font, loadFont]);

  // When scoreDisplayRef: init DOM and let parent update (no re-renders)
  useEffect(() => {
    if (scoreDisplayRef?.current) scoreDisplayRef.current.textContent = '0';
  }, [scoreDisplayRef]);

  // Get position styles - using wrapper approach for center to work with framer-motion
  const getPositionStyles = () => {
    const base = { position: 'absolute', zIndex: 50 };
    switch (position) {
      case 'top-left':
        return { wrapper: { ...base, top: '16px', left: '16px' }, inner: {} };
      case 'top-right':
        return { wrapper: { ...base, top: '16px', right: '16px' }, inner: {} };
      case 'top-center':
      default:
        // Use wrapper to center, so inner animation doesn't break centering
        return { 
          wrapper: { ...base, top: '16px', left: '0', right: '0', display: 'flex', justifyContent: 'center' }, 
          inner: {} 
        };
    }
  };

  // Responsive Tailwind text sizes – slightly reduced to align with start/end card scale
  const sizeClasses = {
    small: 'text-base md:text-lg py-1 px-3',
    medium: 'text-lg md:text-xl py-1.5 px-4',
    large: 'text-xl md:text-2xl py-2 px-5',
  };
  const sizeClass = sizeClasses[size] || sizeClasses.medium;

  const frameStyles = showBackground ? getScorePanelStyles(frameStyle, colors) : {};
  const { wrapper: wrapperStyles, inner: innerStyles } = getPositionStyles();

  const textColor = colors?.text || '#FFFFFF';
  
  // For gradient frame, use white text for better contrast
  const displayColor = frameStyle === 'gradient' ? '#FFFFFF' : textColor;

  return (
    <div style={wrapperStyles}>
      <div
        className={`sf-score-fadein ${className} ${sizeClass} flex items-center gap-2 font-bold select-none pointer-events-none`}
        style={{
          ...innerStyles,
          ...frameStyles,
          fontFamily: `"${font}", sans-serif`,
          color: displayColor,
        }}
      >
      {label && (
        <span style={{ opacity: 0.7, fontSize: '0.75em', fontWeight: '600' }}>
          {label}
        </span>
      )}
      {scoreDisplayRef ? (
        <span
          ref={scoreDisplayRef}
          style={{
            fontVariantNumeric: 'tabular-nums',
            minWidth: '3ch',
            display: 'inline-block',
            textAlign: 'center',
          }}
        >
          0
        </span>
      ) : animate ? (
        <AnimatedScore value={score} font={font} color={displayColor} />
      ) : (
        <span style={{ 
          fontVariantNumeric: 'tabular-nums',
          minWidth: `${Math.max(3, String(score).length)}ch`,
          display: 'inline-block',
          textAlign: 'center'
        }}>
          {score.toLocaleString()}
        </span>
      )}

      {/* CRT scanline effect overlay */}
      {frameStyle === 'crt' && showBackground && (
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.15,
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.4) 2px, rgba(0,0,0,0.4) 4px)',
            borderRadius: '8px',
          }}
        />
      )}
      
      {/* VHS tracking effect overlay */}
      {frameStyle === 'vhs' && showBackground && (
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.1,
            background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(233, 69, 96, 0.3) 3px, rgba(233, 69, 96, 0.3) 6px)',
            borderRadius: '4px',
          }}
        />
      )}
      </div>
    </div>
  );
};

export default GameScoreDisplay;
